// ═════════════════════════════════════════════════════════════════════════════
// LIS mapping API client
//
// Each lab service needs the code its LIS partner uses for it before samples
// can be sent (lis/patient_registration/ sends only *verified* codes). All
// calls are scoped by the backend to the logged-in facility's LIS partner —
// the browser never names a company.
//
//   Partner tests  GET  lis/tests/              [{ code, name }]
//   Mappings       GET  lis/mappings/           one row per test (bed variants grouped)
//   Verify         POST lis/mappings/verify/    { items: [{ service_ids, test_code }] }
//   Reject         POST lis/mappings/reject/    { service_ids }
//   Automap        POST lis/mappings/automap/   refresh suggestions
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import { getBillingHeaders, sanitizeErrorMessage } from "./billing-api";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const LIS_ROOT = `${API_ROOT}lis`;

/** JSON request; a `status: "Failed"` body becomes a thrown Error with the backend message. */
async function request(method, url, body) {
  try {
    const res = await axios({ method, url, data: body, headers: getBillingHeaders(body != null) });
    if (res.data?.status === "Failed") throw new Error(sanitizeErrorMessage(res.data.message || "Request failed"));
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (!err.response) throw err;
    throw new Error(
      sanitizeErrorMessage(err.response.data?.message || err.message || "Request failed", err.response.status),
    );
  }
}

// ── mappers (API → UI) ────────────────────────────────────────────────────────

function mapMapping(m) {
  if (!m) return null;
  return {
    testCode: m.test_code,
    testName: m.test_name || "",
    source: m.source, // exact | fuzzy | manual
    score: m.score,
    status: m.status, // suggested | verified | rejected
    verifiedBy: m.verified_by || "",
    verifiedAt: m.verified_at || null,
  };
}

function mapRow(r) {
  return {
    key: r.key,
    uniqueCode: r.unique_code || "",
    name: r.name || "—",
    serviceIds: r.service_ids || [],
    bedTypes: r.bed_types || [],
    // verified | suggested | rejected | unmapped | partial (bed variants disagree)
    status: r.status,
    mapping: mapMapping(r.mapping),
    // Words such as Urine / IgM / Total that differ between our name and the LIS test.
    checkWords: r.check_words || [],
  };
}

// ── calls ─────────────────────────────────────────────────────────────────────

export async function listLisMappings() {
  const data = await request("GET", `${LIS_ROOT}/mappings/`);
  return {
    partner: data.partner || null,
    rows: (Array.isArray(data.data) ? data.data : []).map(mapRow),
  };
}

export async function listLisTests() {
  const data = await request("GET", `${LIS_ROOT}/tests/`);
  return Array.isArray(data.data) ? data.data : [];
}

/** items: [{ serviceIds, testCode }] — confirms each code for all of its services. */
export async function verifyLisMappings(items) {
  return request("POST", `${LIS_ROOT}/mappings/verify/`, {
    items: items.map((i) => ({ service_ids: i.serviceIds, test_code: i.testCode })),
  });
}

export async function rejectLisMappings(serviceIds) {
  return request("POST", `${LIS_ROOT}/mappings/reject/`, { service_ids: serviceIds });
}

export async function runLisAutomap() {
  const data = await request("POST", `${LIS_ROOT}/mappings/automap/`, {});
  return data.summary || {};
}
